import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectJobChangeOrderDetails, selectChangeOrderParent, selectGeneralProject } from "../../../redux/selectors";
import { Container, Row, Col } from 'react-bootstrap';
import { formatDateForEmail } from "../../../utils/dateUtils";

const ViewChangeOrder = ({ changeOrderId, notes, deletedItemsIds }) => {
    const projectDetails = useSelector(selectGeneralProject);
    const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
    const changeOrderParent = useSelector(selectChangeOrderParent);

    const selectedChangeOrder = changeOrderParent.find(order => order.ID === changeOrderId);
    
    // Filter out deleted items from the change order details
    // Note: fullJobFetch API may return ChangeOrderNumber as undefined instead of null
    const filteredChangeOrderDetails = useMemo(() => {
        if (!changeOrderDetails || !deletedItemsIds) return changeOrderDetails;
        
        return changeOrderDetails.filter(detail => !deletedItemsIds.has(detail.ID));
    }, [changeOrderDetails, deletedItemsIds]);

    // Check if the selected change order has action items, using the filtered details
    const hasActionItems = filteredChangeOrderDetails.some(detail => 
        detail.ChangeOrderNumber === selectedChangeOrder?.ChangeOrderNumber &&
        (detail.ActionRequiredMechanical || detail.ActionRequiredElectrical || detail.ActionRequiredControls || detail.ActionRequiredCSM)
    );

    // Use the filtered details for all subsequent operations
    const includeOnChangeOrder = filteredChangeOrderDetails.filter(detail => 
        detail.ChangeOrderNumber === selectedChangeOrder?.ChangeOrderNumber
    );

    // console.log(includeOnChangeOrder);

    // noBill includes items with no billing info OR undefined BillCreditTo (from fullJobFetch API)
    const noBill = includeOnChangeOrder.filter(detail => (detail.BillCreditTo === "" || detail.BillCreditTo === null || detail.BillCreditTo === "N/A" || detail.BillCreditTo === undefined));
    const watertronicsBill = includeOnChangeOrder.filter(detail => detail.BillCreditTo === "Watertronics");
    const customerBill = includeOnChangeOrder.filter(detail => detail.BillCreditTo === "Customer");

    const getFirstDefinedValue = (detail, keys) => {
        for (const key of keys) {
            const value = detail?.[key];
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return undefined;
    };

    const toDisplayValue = (value, fallback = "BLANK") => {
        return value === undefined || value === null || value === "" ? fallback : value;
    };

    const normalizeLabelPart = (value) => {
        if (value === undefined || value === null) {
            return "";
        }

        const trimmedValue = String(value).trim();
        if (trimmedValue === "" || trimmedValue.toUpperCase() === "BLANK") {
            return "";
        }

        const withoutTrailingPlaceholder = trimmedValue.replace(/\s+BLANK$/i, "").trim();
        return withoutTrailingPlaceholder.toUpperCase() === "BLANK" ? "" : withoutTrailingPlaceholder;
    };

    const getDetailTitle = (detail) => normalizeLabelPart(getFirstDefinedValue(detail, ["Title", "title"]));
    const getDetailField = (detail) => normalizeLabelPart(getFirstDefinedValue(detail, ["Field", "field", "FieldName", "fieldName", "field_name"]));
    const getDetailExistingValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["ExistingValue", "existingValue", "PreviousValue", "previousValue", "PrevValue", "prevValue", "existing_value", "previous_value"]));
    const getDetailNewValue = (detail) => toDisplayValue(getFirstDefinedValue(detail, ["NewValue", "newValue", "UpdatedValue", "updatedValue", "new_value", "updated_value"]));
    const getChangeLabel = (detail) => {
        const labelParts = [getDetailTitle(detail), getDetailField(detail)].filter(Boolean);
        return labelParts.length > 0 ? labelParts.join(" ") : "BLANK";
    };

    const groupAndSortByReason = (details) => {
        const grouped = details.reduce((acc, detail) => {
            const reason = detail.ReasonForChange || "Reason Not Assigned";
            if (!acc[reason]) {
                acc[reason] = [];
            }
            acc[reason].push(detail);
            return acc;
        }, {});

        return Object.keys(grouped).sort().map(reason => ({
            reason,
            details: grouped[reason]
        }));
    };

    const groupedNoBill = groupAndSortByReason(noBill);
    const groupedCustomerBill = groupAndSortByReason(customerBill);
    const groupedWatertronicsBill = groupAndSortByReason(watertronicsBill);

    const actionRequiredDepartments = [];
    if (filteredChangeOrderDetails.some(detail => detail.ActionRequiredMechanical)) {
        actionRequiredDepartments.push(`${projectDetails[0]?.MechanicalEngineer || "Mechanical Engineering"} (Mechanical Engineering)`);
    }
    if (filteredChangeOrderDetails.some(detail => detail.ActionRequiredElectrical)) {
        actionRequiredDepartments.push(`${projectDetails[0]?.ElectricalEngineer || "Electrical Engineering"} (Electrical Engineering)`);
    }
    if (filteredChangeOrderDetails.some(detail => detail.ActionRequiredControls)) {
        actionRequiredDepartments.push(`${projectDetails[0]?.ControlsEngineer || "Controls Engineering"} (Controls Engineering)`);
    }
    if (filteredChangeOrderDetails.some(detail => detail.ActionRequiredCSM)) {
        actionRequiredDepartments.push("CSM");
    }

    const formatDepartments = (departments) => {
        if (departments.length === 2) {
            return departments.join(" & ");
        } else if (departments.length > 2) {
            return departments.slice(0, -1).join(", ") + " & " + departments.slice(-1);
        }
        return departments.join(", ");
    };

    const calculateTotal = (details, field, costCreditField) => {
        return details.reduce((acc, detail) => {
            const value = detail[field] * (detail[costCreditField] ? 1 : -1);
            return acc + value;
        }, 0);
    };

    const customerManufacturingLaborTotal = calculateTotal(customerBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
    const customerEngineeringLaborTotal = calculateTotal(customerBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
    const customerMaterialsVarianceTotal = calculateTotal(customerBill, 'MaterialsVariance', 'MaterialCostCredit');
    const customerScrapTotal = calculateTotal(customerBill, 'Scrap', 'ScrapCostCredit');

    const watertronicsManufacturingLaborTotal = calculateTotal(watertronicsBill, 'ManufacturingLaborHours', 'ManufacturingCostCredit') * 90;
    const watertronicsEngineeringLaborTotal = calculateTotal(watertronicsBill, 'EngineeringLaborHours', 'EngineeringCostCredit') * 165;
    const watertronicsMaterialsVarianceTotal = calculateTotal(watertronicsBill, 'MaterialsVariance', 'MaterialCostCredit');
    const watertronicsScrapTotal = calculateTotal(watertronicsBill, 'Scrap', 'ScrapCostCredit');

    const totalManufacturingLabor = customerManufacturingLaborTotal + watertronicsManufacturingLaborTotal;
    const totalEngineeringLabor = customerEngineeringLaborTotal + watertronicsEngineeringLaborTotal;
    const totalMaterialsVariance = customerMaterialsVarianceTotal + watertronicsMaterialsVarianceTotal;
    const totalScrap = customerScrapTotal + watertronicsScrapTotal;

    const customerBillTotal = customerManufacturingLaborTotal + customerEngineeringLaborTotal + customerMaterialsVarianceTotal + customerScrapTotal;
    const watertronicsBillTotal = watertronicsManufacturingLaborTotal + watertronicsEngineeringLaborTotal + watertronicsMaterialsVarianceTotal + watertronicsScrapTotal;

    const formatCostCredit = (value) => {
        return value < 0 ? `${Math.abs(value).toFixed(2)} Cost` : `${value.toFixed(2)} Credit`;
    };

    const formatDate = (dateString) => {
        return formatDateForEmail(dateString);
    };
    
    // Check if data is loaded
    if (!changeOrderDetails || changeOrderDetails.length === 0) {
        return <div>Loading change order details...</div>;
    }
    
    return (
        <Container className="change-order-document change-order-view">
            {projectDetails && selectedChangeOrder ? (
                <>
                    <div className="text-center">
                        <Row>
                            <Col>
                                <h1><strong>Change Order # {selectedChangeOrder.ChangeOrderNumber}</strong></h1>
                                <h3>{selectedChangeOrder.JobNumber} - {selectedChangeOrder.JobName}</h3>
                                <p><strong>Written By:</strong> {selectedChangeOrder.WrittenBy} on {formatDate(selectedChangeOrder.DateSubmitted)}. <strong>Sales Rep:</strong> {selectedChangeOrder.SalesPerson}</p>
                                <hr />
                            </Col>
                        </Row>
                    </div>
                    {hasActionItems && actionRequiredDepartments.length > 0 && (
                        <div>
                            <p><span style={{ color: 'red', fontWeight: 'bold' }}>This change order includes assigned action items for {formatDepartments(actionRequiredDepartments)}. Please review Change Order Tasks in the Production Portal for more information.</span></p>
                            <hr />
                        </div>
                    )}
                    {groupedNoBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes</h2>
                            <ol>
                                {groupedNoBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {getChangeLabel(detail)} <span style={{ color: 'red' }}>changed from</span> {getDetailExistingValue(detail)} <span style={{ color: 'red' }}>to</span> {getDetailNewValue(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    {groupedCustomerBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Customer</h2>
                            <ol>
                                {groupedCustomerBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {getChangeLabel(detail)} <span style={{ color: 'red' }}>changed from</span> {getDetailExistingValue(detail)} <span style={{ color: 'red' }}>to</span> {getDetailNewValue(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    {groupedWatertronicsBill.length > 0 && (
                        <div>
                            <h2 className="mt-3">Changes Billed to Watertronics</h2>
                            <ol>
                                {groupedWatertronicsBill.map(group => (
                                    <div key={group.reason}>
                                        <h4>{group.reason}</h4>
                                        {group.details.map((detail, index) => (
                                            <li key={index}>
                                                <p className="mt-2 mb-2">
                                                    {getChangeLabel(detail)} <span style={{ color: 'red' }}>changed from</span> {getDetailExistingValue(detail)} <span style={{ color: 'red' }}>to</span> {getDetailNewValue(detail)}
                                                </p>
                                            </li>
                                        ))}
                                    </div>
                                ))}
                            </ol>
                        </div>
                    )}
                    {(totalManufacturingLabor !== 0 || totalEngineeringLabor !== 0 || totalMaterialsVariance !== 0 || totalScrap !== 0 || customerBillTotal !== 0 || watertronicsBillTotal !== 0) && (
                        <div>
                            <h2 className="mt-3">Cost To/ Credit To</h2>
                            {totalManufacturingLabor !== 0 && (
                                <p><strong>Manufacturing Labor Total ($90/hr):</strong> ${formatCostCredit(totalManufacturingLabor)}</p>
                            )}
                            {totalEngineeringLabor !== 0 && (
                                <p><strong>Engineering Labor Total ($165/hr):</strong> ${formatCostCredit(totalEngineeringLabor)}</p>
                            )}
                            {totalMaterialsVariance !== 0 && (
                                <p><strong>Materials Variance Total:</strong> ${formatCostCredit(totalMaterialsVariance)}</p>
                            )}
                            {totalScrap !== 0 && (
                                <p><strong>Scrap Total:</strong> ${formatCostCredit(totalScrap)}</p>
                            )}
                            {customerBillTotal !== 0 && (
                                <p><strong>Customer Bill Total:</strong> ${formatCostCredit(customerBillTotal)}</p>
                            )}
                            {watertronicsBillTotal !== 0 && (
                                <p><strong>Watertronics Bill Total:</strong> ${formatCostCredit(watertronicsBillTotal)}</p>
                            )}
                            <hr />
                        </div>
                    )}
                    {notes && (
                        <div className="mt-3">
                            <strong>Notes:</strong>
                            <p>{notes}</p>
                        </div>
                    )}
                </>
            ) : (
                <p>Loading project details...</p>
            )}
        </Container>
    );
};

export default ViewChangeOrder;
